import React, { useContext, useEffect, useState } from 'react'
import MaterialTable from 'material-table'
import { SettingsContext, StoreContext } from '../../contexts'
import { ProposalDetailPanel } from './DetailPanels'
import { Check as CheckIcon } from '@material-ui/icons'
import { Tooltip, TableCell } from '@material-ui/core'
import { CsvBuilder } from 'filefy'
import { jsPDF } from 'jspdf'
import { ensureColumnWidths } from '../../utils'

const headerWithTooltip = (title, tooltip) => (
    <Tooltip title={tooltip} placement='top'>
        <TableCell className="MTableHeader-header-350">{title}</TableCell>
    </Tooltip>
)

// Every column here uses headerWithTooltip, so column.title is a React element.
// material-table's default PDF export does `head: [columns.map(c => c.title)]`,
// which stringifies those to "[object Object]". Recover the plain label.
const columnLabel = column => {
    if (typeof column.title === 'string') return column.title
    // headerWithTooltip renders <Tooltip><TableCell>{label}</TableCell></Tooltip>
    const label = column.title?.props?.children?.props?.children
    return typeof label === 'string' ? label : column.field ?? ''
}

const exportCsv = (columns, rows) => {
    // determine which headers are visible in the UI.
    const columnHeaders = columns
        .filter(column => !column.hidden)
        .map(column => column.field)
    // grab the non-hidden columns (row properties) only for export.
    const reducedRows = rows.map(row => {
        const reducedRow = {}
        columnHeaders.forEach(header => {
            reducedRow[header] = row[header]
        })
        return reducedRow
    })
    // put these rows into arrays like [id, title, status, description, ...]
    const data = reducedRows.map(row => Object.values(row))
    // instantiate new builder.
    const builder = new CsvBuilder('proposals.csv')
    // build and export the CSV file.
    builder
        .setDelimeter(',')
        .setColumns(columnHeaders)
        .addRows(data)
        .exportFile()
}

// Mirrors material-table's defaultExportPdf, but with plain-text headers instead
// of the JSX column titles (which render as "[object Object]").
const exportPdf = (columns, rows) => {
    const visibleColumns = columns.filter(column => !column.hidden && column.field)
    const doc = new jsPDF('landscape', 'pt', 'A4')
    doc.setFontSize(15)
    doc.text('proposals', 40, 40)
    doc.autoTable({
        startY: 50,
        head: [visibleColumns.map(columnLabel)],
        body: rows.map(row =>
            visibleColumns.map(column => {
                const value = row[column.field]
                return value === null || value === undefined ? '' : String(value)
            })
        ),
    })
    doc.save('proposals.pdf')
}

export const ProposalsTable = ({ title, proposals, components, ...props }) => {
    const [settings] = useContext(SettingsContext)
    const [{ services }] = useContext(StoreContext)

    useEffect(() => {
        if (proposals) {
            // Add property for each resource to each proposal, identify as requested, approved, or neither
            proposals.forEach(proposal => {
                services.forEach(resource => {
                    proposal[resource] = proposal.approvedServices.includes(resource) ? 'Approved' : (proposal.requestedServices.includes(resource) ? 'Requested' : '')
                })
            })
        }

    }, [proposals])

    if (title) title += ` (${ proposals.length } Proposals)`
    
    return (
        <MaterialTable
            title={ title || null }
            components={{ ...components }}
            columns={ ensureColumnWidths([
                {
                    title: headerWithTooltip('ID', 'Proposal ID'),
                    field: 'proposalID',
                    hidden: !settings.tables.visibleColumns.proposalID,
                },
                {
                    title: headerWithTooltip('Proposal Name', 'Short title of the TIN project proposal'),
                    field: 'shortTitle',
                    hidden: !settings.tables.visibleColumns.shortTitle,
                },
                {
                    title: headerWithTooltip('PI', 'Principal Investigator'),
                    field: 'piName',
                    hidden: !settings.tables.visibleColumns.piName,
                },
                {
                    title: headerWithTooltip('Proposal Status', 'Proposal Status'),
                    field: 'proposalStatus',
                    hidden: !settings.tables.visibleColumns.proposalStatus,
                },
                {
                    title: headerWithTooltip('Therapeutic Area', 'Therapeutic area of the study'),
                    field: 'therapeuticArea',
                    hidden: !settings.tables.visibleColumns.therapeuticArea,
                },
                {
                    title: headerWithTooltip('Submitting Institution', "Submitter's organization name"),
                    field: 'submitterInstitution',
                    hidden: !settings.tables.visibleColumns.submitterInstitution,
                },
                {
                    title: headerWithTooltip('Assigned TIC/RIC', 'TIC/RIC to which the study is assigned to carry out the consultation'),
                    field: 'assignToInstitution',
                    hidden: !settings.tables.visibleColumns.assignToInstitution,
                },
                {
                    title: headerWithTooltip('Submission Date', 'Date proposal was submitted to TIN'),
                    field: 'dateSubmitted',
                    hidden: !settings.tables.visibleColumns.dateSubmitted, 
                },
                {
                    title: headerWithTooltip('PAT Review Date', 'Proposal Assessment Team Review Date'),
                    field: 'meetingDate',
                    hidden: !settings.tables.visibleColumns.meetingDate,
                },
                {
                    title: headerWithTooltip('Planned Grant Submission Date', 'Planned Grant Submission Date'),
                    field: 'plannedGrantSubmissionDate',
                    hidden: !settings.tables.visibleColumns.plannedGrantSubmissionDate,
                },
                {
                    title: headerWithTooltip('Actual Grant Submission Date', 'Actual Grant Submission Date'),
                    field: 'actualGrantSubmissionDate',
                    hidden: !settings.tables.visibleColumns.actualGrantSubmissionDate,
                },                
                {
                    title: headerWithTooltip('Kick-off Meeting Date', 'Kick-off Meeting Date'),
                    field: 'kickOff',
                    hidden: !settings.tables.visibleColumns.kickOff,
                },
                {
                    title: headerWithTooltip('Initial Contact Date', 'Initial Contact Date'),
                    field: 'firstContact',
                    hidden: !settings.tables.visibleColumns.firstContact,
                },
                {
                    title: headerWithTooltip('Funding Source', 'Current funding source of the study/trial'),
                    field: 'fundingSource',
                    hidden: !settings.tables.visibleColumns.fundingSource,
                },
                // {
                //     title: headerWithTooltip('New Funding Source', 'New Funding Source'),
                //     field: 'newFundingSource',
                //     hidden: !settings.tables.visibleColumns.newFundingSource,
                // },
                {
                    title: headerWithTooltip('Funding Status', 'New Funding Status'),
                    field: 'fundingStatus',
                    hidden: !settings.tables.visibleColumns.fundingStatus,
                },
                {
                    title: headerWithTooltip('Funding Amount', 'Total amount awarded for the study'),
                    field: 'fundingAmount',
                    hidden: !settings.tables.visibleColumns.fundingAmount,
                },
                {
                    title: headerWithTooltip('Funding Period', 'Total duration of the funding period'),
                    field: 'fundingPeriod',
                    hidden: !settings.tables.visibleColumns.fundingPeriod,
                },
                {
                    title: headerWithTooltip('Estimated Funding Start Date', 'Projected/Estimated Funding Start Date'),
                    field: 'estimatedFundingStartDate',
                    hidden: !settings.tables.visibleColumns.estimatedFundingStartDate,
                },
                {
                    title: headerWithTooltip('Actual Funding Start Date', 'Actual Funding Start Date'),
                    field: 'actualFundingStartDate',
                    hidden: !settings.tables.visibleColumns.actualFundingStartDate,
                },
                // {
                //     title: headerWithTooltip('Recommend for Comprehensive Consultation', 'Recommend for Comprehensive Consultation'),
                //     field: 'approvedForComprehensiveConsultation',
                //     hidden: !settings.tables.visibleColumns.approvedForComprehensiveConsultation,
                //     // filtering: false,
                // },
                {
                    title: headerWithTooltip('Study Population', 'Study Population Category'),
                    field: 'studyPopulation',
                    hidden: !settings.tables.visibleColumns.studyPopulation,
                },
                {
                    title: headerWithTooltip('Phase', 'Study Phase'),
                    field: 'phase',
                    hidden: !settings.tables.visibleColumns.phase,
                },
                {
                    title: headerWithTooltip('Funding Insitute 1', 'Institute/Center for first funding source'),
                    field: 'fundingInstitute',
                    hidden: !settings.tables.visibleColumns.fundingInstitute,
                },
                {
                    title: headerWithTooltip('Funding Insitute 2', 'Insitute/Center for second funding source'),
                    field: 'fundingInstitute2',
                    hidden: !settings.tables.visibleColumns.fundingInstitute2,
                },
                {
                    title: headerWithTooltip('Funding Insitute 3', 'Insitute/Center for third funding source'),
                    field: 'fundingInstitute3',
                    hidden: !settings.tables.visibleColumns.fundingInstitute3,
                },
                {
                    title: headerWithTooltip('Funding Source Confirmation', 'Confirm whether the anticipated funding source is same or different'),
                    field: 'fundingSourceConfirmation',
                    hidden: !settings.tables.visibleColumns.fundingSourceConfirmation,
                },
                {
                    title: headerWithTooltip('Number of CTSA Program Hub Sites', 'Number of CTSA Program Hub Sites'),
                    field: 'numberCTSAprogHubSites',
                    hidden: !settings.tables.visibleColumns.numberCTSAprogHubSites,
                },
                {
                    title: headerWithTooltip('Number of Sites', 'Estimated number of Sites'),
                    field: 'numberSites',
                    hidden: !settings.tables.visibleColumns.numberSites,
                },

                {
                    title: headerWithTooltip('Actual Protocol Final Date', 'Actual Protocol Final Date'),
                    field: 'actualProtocolFinalDate',
                    hidden: !settings.tables.visibleColumns.actualProtocolFinalDate,
                },
                {
                    title: headerWithTooltip('Actual Grant Award Date', 'Actual Grant Award Date'),
                    field: 'actualGrantAwardDate',
                    hidden: !settings.tables.visibleColumns.actualGrantAwardDate,
                },
                {
                    title: headerWithTooltip('Approval Release Diff', 'Difference between PAT approval and actual release of funds'),
                    field: 'approvalReleaseDiff',
                    hidden: !settings.tables.visibleColumns.approvalReleaseDiff,
                },
                {
                    title: headerWithTooltip('COVID Study', 'Is this a COVID related study?'),
                    field: 'covidStudy',
                    hidden: !settings.tables.visibleColumns.covidStudy,
                },
                {
                    title: headerWithTooltip('HEAL Study', 'Is this a HEAL network study?'),
                    field: 'healStudy',
                    hidden: !settings.tables.visibleColumns.healStudy,
                },
            ].concat(services.map(
                resource => ({
                        title: `Resource: ${ resource }`,
                        field: resource,
                        hidden: !settings.tables.visibleColumns.resources,
                })
              )))
            }
            data={ proposals }
            options={{
                paging: false,
                columnsButton: true,
                exportButton: true,
                filtering: true,
                grouping: true,
                exportFileName: title || 'proposals',
                groupTitle: rowData => {
                    return 'TITLE'
                },
                exportCsv: exportCsv,
                exportPdf: exportPdf,
            }}
            detailPanel={ rowData => <ProposalDetailPanel { ...rowData } />}
            onRowClick={ (event, rowData, togglePanel) => togglePanel() }
        />
    )
}
